import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IRecord } from '@platform/core/types';
import { Picker } from '@platform/ui';
import { useListAllRecordsQuery } from '@platform/state/api';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import type { IControlProps } from '../IControlProps';
import { LookupFooter } from '../lookup/LookupFooter';
import { QuickCreateDialog } from '../lookup/QuickCreateDialog';
import { RecordSearchDialog } from '../lookup/RecordSearchDialog';
import { useRecordChoices } from '../lookup/useRecordChoices';
import { descendantsOf } from '../options/descendantsOf';

/** Значення пункту «+ Створити «…»» у списку (не ідентифікатор запису). */
const CREATE = '\u0000create';

/**
 * Посилання на запис (UIUX/field-types/lookup; прототип ref-пікер): поле «код · назва», список «Немає» + записи цільового типу з
 * уточненням «статус · колонки варіанта lookup», пошук за кодом або назвою на сервері з першого символу, підвал «N з M» і
 * «Розширений пошук…»; введений текст без точного збігу можна створити як новий запис («+ Створити «…»», крім довідників).
 * Поле «батько» (посилання на свій тип) не пропонує сам запис і всіх його нащадків (діти, онуки… — за всіма записами типу, коли список
 * відкрито). Значення — ідентифікатор запису.
 */
export function RecordLookup({ id, value, onChange, readOnly, field, rt, ws, size, prefix, invalid, placeholder, record, autoFocus }: IControlProps<string>) {
  const { t } = useTranslation();
  const strings = useModuleStrings();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const [creating, setCreating] = useState<string | null>(null);
  const target = field.ref ?? '';
  const self = record && field.ref === rt.key ? record.id : '';
  const tree = useListAllRecordsQuery({ ws, rt: target }, { skip: !self || !(open || advanced) });
  const below = useMemo(() => (self && tree.data ? descendantsOf(tree.data.items, self, field.key) : new Set<string>()), [self, tree.data, field.key]);
  const exclude = useCallback((r: IRecord) => Boolean(self) && (r.id === self || r.values[field.key] === self || below.has(r.id)), [self, field.key, below]);
  const c = useRecordChoices(ws, target, value ?? '', open, query, exclude);
  const label = strings.field(rt.key, field.key, field.label);
  const typed = query.trim();
  const canCreate = Boolean(typed) && !readOnly && c.targetRt?.kind !== 'catalogue' && !c.options.some((o) => o.label.toLowerCase().endsWith(` · ${typed.toLowerCase()}`));
  const options = [{ value: '', label: t('fieldControls.none'), muted: true }, ...c.options,
    ...(canCreate ? [{ value: CREATE, label: t('fieldControls.createRecord', { name: typed }), icon: 'plus', accent: true }] : [])];
  const pick = (v: string) => { if (v === CREATE) setCreating(typed); else onChange(v || null); };
  return (
    <>
      <Picker id={id} value={value ?? ''} options={options} selected={c.selected} onChange={pick} label={label}
        prefix={prefix} size={size} readOnly={readOnly || !target} invalid={invalid} autoFocus={autoFocus}
        placeholder={placeholder ?? t('fieldControls.none')} query={query} onQueryChange={setQuery} onOpenChange={setOpen} loading={c.loading}
        footer={<LookupFooter count={c.options.length} total={c.total} onAdvanced={() => setAdvanced(true)} />} />
      {advanced && (
        <RecordSearchDialog ws={ws} target={target} title={label} exclude={exclude}
          onPick={(rid) => { onChange(rid); setAdvanced(false); }} onClose={() => setAdvanced(false)} />
      )}
      {creating !== null && (
        <QuickCreateDialog ws={ws} target={target} title={creating} onClose={() => setCreating(null)}
          onCreated={(rec) => { setCreating(null); onChange(rec.id); }} />
      )}
    </>
  );
}
